import { TooltipRenderProps } from 'react-joyride';
import { CSSProperties, useEffect, useRef, useState } from 'react';

import {
  ArrowPosition,
  Offset,
  StepData,
  TooltipAlign
} from '@/providers/JoyrideProvider/JoyrideProvider.tsx';

const ARROW_SIZE = 5;
const TOOLTIP_COLOR = '#FDC23B';
const DEFAULT_TOOLTIP_DISTANCE = 0;

// Calculate arrow position to point at target center
const calculateArrowPosition = (
  tooltipRect: DOMRect,
  targetRect: DOMRect,
  arrowPosition: Exclude<ArrowPosition, 'none'>,
  manualOffset: Offset = {}
): CSSProperties => {
  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;

  const { x: offsetX = 0, y: offsetY = 0 } = manualOffset;

  switch (arrowPosition) {
    case 'top': {
      // Arrow on top edge, pointing up
      const relativeX = targetCenterX - tooltipRect.left + offsetX;
      const clampedX = Math.max(
        ARROW_SIZE,
        Math.min(relativeX, tooltipRect.width - ARROW_SIZE)
      );
      return {
        top: -ARROW_SIZE,
        left: clampedX,
        transform: 'translateX(-50%)',
        borderWidth: `0 ${ARROW_SIZE}px ${ARROW_SIZE}px ${ARROW_SIZE}px`,
        borderColor: `transparent transparent ${TOOLTIP_COLOR} transparent`
      };
    }
    case 'bottom': {
      // Arrow on bottom edge, pointing down
      const relativeX = targetCenterX - tooltipRect.left + offsetX;
      const clampedX = Math.max(
        ARROW_SIZE,
        Math.min(relativeX, tooltipRect.width - ARROW_SIZE)
      );
      return {
        bottom: -ARROW_SIZE,
        left: clampedX,
        transform: 'translateX(-50%)',
        borderWidth: `${ARROW_SIZE}px ${ARROW_SIZE}px 0 ${ARROW_SIZE}px`,
        borderColor: `${TOOLTIP_COLOR} transparent transparent transparent`
      };
    }
    case 'left': {
      // Arrow on left edge, pointing left
      const relativeY = targetCenterY - tooltipRect.top + offsetY;
      const clampedY = Math.max(
        ARROW_SIZE,
        Math.min(relativeY, tooltipRect.height - ARROW_SIZE)
      );
      return {
        left: -ARROW_SIZE,
        top: clampedY,
        transform: 'translateY(-50%)',
        borderWidth: `${ARROW_SIZE}px ${ARROW_SIZE}px ${ARROW_SIZE}px 0`,
        borderColor: `transparent ${TOOLTIP_COLOR} transparent transparent`
      };
    }
    case 'right': {
      // Arrow on right edge, pointing right
      const relativeY = targetCenterY - tooltipRect.top + offsetY;
      const clampedY = Math.max(
        ARROW_SIZE,
        Math.min(relativeY, tooltipRect.height - ARROW_SIZE)
      );
      return {
        right: -ARROW_SIZE,
        top: clampedY,
        transform: 'translateY(-50%)',
        borderWidth: `${ARROW_SIZE}px 0 ${ARROW_SIZE}px ${ARROW_SIZE}px`,
        borderColor: `transparent transparent transparent ${TOOLTIP_COLOR}`
      };
    }
  }
};

// Determine arrow position based on tooltip placement
const getArrowPositionFromPlacement = (
  placement: string
): Exclude<ArrowPosition, 'none'> => {
  if (placement.startsWith('top')) return 'bottom';
  if (placement.startsWith('bottom')) return 'top';
  if (placement.startsWith('left')) return 'right';
  if (placement.startsWith('right')) return 'left';
  return 'bottom';
};

// Calculate tooltip transform based on alignment and offset
const getTooltipTransform = (
  placement: string,
  align: TooltipAlign,
  offset: Offset,
  distance: number
): string => {
  const transforms: string[] = [];

  const offsetX = offset.x || 0;
  const offsetY = offset.y || 0;

  // Apply distance based on placement direction
  let distanceX = 0;
  let distanceY = 0;

  if (placement.startsWith('top')) {
    distanceY = -distance;
  } else if (placement.startsWith('bottom')) {
    distanceY = distance;
  } else if (placement.startsWith('left')) {
    distanceX = -distance;
  } else if (placement.startsWith('right')) {
    distanceX = distance;
  }

  // Apply alignment offset for horizontal placements (top/bottom)
  let alignOffsetX = 0;
  let alignOffsetY = 0;

  if (placement.startsWith('top') || placement.startsWith('bottom')) {
    switch (align) {
      case 'start':
        alignOffsetX = -30; // Shift left
        break;
      case 'end':
        alignOffsetX = 30; // Shift right
        break;
    }
  } else if (placement.startsWith('left') || placement.startsWith('right')) {
    switch (align) {
      case 'start':
        alignOffsetY = -20; // Shift up
        break;
      case 'end':
        alignOffsetY = 20; // Shift down
        break;
    }
  }

  const totalX = offsetX + distanceX + alignOffsetX;
  const totalY = offsetY + distanceY + alignOffsetY;

  if (totalX !== 0 || totalY !== 0) {
    transforms.push(`translate(${totalX}px, ${totalY}px)`);
  }

  return transforms.join(' ') || 'none';
};

export const JoyrideTooltip = ({ step, tooltipProps }: TooltipRenderProps) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [arrowStyle, setArrowStyle] = useState<CSSProperties>({});

  const placement = step.placement || 'top';
  const stepData = (step.data as StepData) || {};

  // Arrow configuration
  const hideArrow = stepData.hideArrow || false;
  const arrowOffset = stepData.arrowOffset || {};
  const arrowPosition: ArrowPosition =
    stepData.arrowPosition || getArrowPositionFromPlacement(placement);

  // Tooltip configuration
  const tooltipOffset = stepData.tooltipOffset || { x: 0, y: 0 };
  const tooltipWidth = stepData.tooltipWidth || 250;
  const tooltipAlign = stepData.tooltipAlign || 'center';
  const tooltipDistance = stepData.tooltipDistance ?? DEFAULT_TOOLTIP_DISTANCE;
  const tooltipPadding = stepData.tooltipPadding || '8px 10px';
  const tooltipBorderRadius = stepData.tooltipBorderRadius ?? 10;

  const showArrow = !hideArrow && arrowPosition !== 'none';
  const transform = getTooltipTransform(
    placement,
    tooltipAlign,
    tooltipOffset,
    tooltipDistance
  );

  // Calculate arrow position after tooltip is rendered
  useEffect(() => {
    if (!showArrow || !tooltipRef.current) return;

    const updateArrowPosition = () => {
      const tooltipRect = tooltipRef.current?.getBoundingClientRect();
      if (!tooltipRect) return;

      const target = step.target;
      let targetElement: HTMLElement | null = null;

      if (target instanceof HTMLElement) {
        targetElement = target;
      } else if (typeof target === 'string') {
        targetElement = document.querySelector<HTMLElement>(target);
      }

      if (!targetElement) return;

      const targetRect = targetElement.getBoundingClientRect();

      const style = calculateArrowPosition(
        tooltipRect,
        targetRect,
        arrowPosition,
        arrowOffset
      );

      setArrowStyle(style);
    };

    const timer = setTimeout(updateArrowPosition, 10);

    return () => clearTimeout(timer);
  }, [showArrow, step.target, arrowPosition, arrowOffset]);

  return (
    <div
      {...tooltipProps}
      ref={tooltipRef}
      style={{
        background: TOOLTIP_COLOR,
        color: '#000000',
        padding: tooltipPadding,
        borderRadius: tooltipBorderRadius,
        fontSize: 13,
        textAlign: 'left',
        position: 'relative',
        maxWidth: tooltipWidth,
        transform
      }}
    >
      {showArrow && (
        <div
          style={{
            position: 'absolute',
            borderStyle: 'solid',
            ...arrowStyle
          }}
        />
      )}
      <div>{step.content}</div>
    </div>
  );
};